import { isNodeShownOnChart, resolveNodeOverlay } from '../config/nodeVisualConfig';
import visualDataKeys from '../config/visualDataKeys.json';
// Utility: chartDataDerivation.js
// Purpose: Pure projection helpers that turn a strategy-graph (nodes + edges)
//          into SmartChart-ready visual layers and indicator overlays. Used by
//          both Strategy Builder (backtest) and Live Analysis — shared source
//          of truth so both surfaces render identically.

//--> Visual presentation node types — used by both deriveVisualLayers below and
//*  the SmartChart graphNodeIds gate in SmartChart.jsx. Single source of truth so
//*  the two stay in sync if a new visual node type is introduced.
const VISUAL_NODE_TYPES = ['line_node', 'area_node', 'candle_node', 'heikin_node', 'volume_node'];

//--> Legacy chart-container types. `chart_node` was the original type; `chart_output`
//*  is the canonical replacement (default seeded by useNodeFactory). Saved graphs
//*  predating the rename still reference `chart_node` — treated as a synonym here
//*  so loose-nodes fallback isn't the only path for legacy graphs.
const CHART_CONTAINER_TYPES = ['chart_output', 'chart_node'];
const isChartContainer = (n) => n?.type && CHART_CONTAINER_TYPES.includes(n.type);

//--> 50-6 §C — ONE authority for which node types are eligible for the Live Analysis
//*  indicator layer / panel row list. Was an inline array in deriveStrategyIndicators'
//*  own .filter(...); exported so IndicatorsPanelTab (R5.1) reads the SAME list rather
//*  than re-declaring it.
export const ELIGIBLE_LAYER_NODE_TYPES = ['indicator', 'math', 'logic_gate', 'comparator', 'signal', 'position_trigger'];

export const deriveVisualLayers = (nodes, edges) => {
  if (!Array.isArray(nodes) || nodes.length === 0) return [];

  const chartNode =
    nodes.find(n => isChartContainer(n) && n.data?.panel === 'main') ||
    nodes.find(isChartContainer);
  let layers = [];

  if (chartNode) {
      const connectedSourceIds = new Set((edges || []).filter(e => e?.target === chartNode.id).map(e => e.source));

      const isVisualNode = (n) => VISUAL_NODE_TYPES.includes(n.type);

      //--> Tighter visibility filter (Option-A streamlining): require explicit
      //*  is_visible === true OR an edge into the chart container OR
      //*  visual_settings.showOnChart. Previous form `is_visible !== undefined`
      //*  let nodes through whose is_visible was explicitly false, generating
      //*  a layer that then got `visible: false` style — wasted series + setData.
      const visualNodes = nodes.filter(n =>
        isVisualNode(n) && (
          connectedSourceIds.has(n.id) ||
          n.data?.is_visible === true ||
          n.data?.visual_settings?.showOnChart
        )
      );

      layers = visualNodes.map(sourceNode => {
          let type;
          if (sourceNode.data?.chartType) {
              type = sourceNode.data.chartType;
          } else if (sourceNode.type === 'line_node') type = 'line';
          else if (sourceNode.type === 'area_node') type = 'area';
          else if (sourceNode.type === 'candle_node') type = 'candle';
          else if (sourceNode.type === 'heikin_node') type = 'heikin';
          else if (sourceNode.type === 'volume_node') type = 'volume';
          else type = 'candle';

          return {
              id: sourceNode.id,
              type: type,
              styleOptions: {
                  ...(sourceNode.data || {}),
                  visible: sourceNode.data?.is_visible !== false, // Default true unless explicitly false
              },
          };
      }).filter(Boolean);
  }

  if (layers.length === 0) {
      const looseNodes = nodes.filter(n => VISUAL_NODE_TYPES.includes(n?.type));
      if (looseNodes.length > 0) {
          const lastNode = looseNodes[looseNodes.length - 1];
          const type = (lastNode.type || 'candle_node').replace('_node', '');
          layers = [{ id: lastNode.id, type, styleOptions: lastNode.data || {} }];
      }
  }

  return layers;
};

//* 142-42: ONE authority for an output's default style — backend indicator metadata
//*   (visual_settings.output_styles) then the node's own visual_settings.output_styles.
//*   Used by the chart derivation AND the Component Styles panel; user outputStyles win per key on top.
//! 142-49 slice 4 — ONE default-colour authority for every output of every indicator.
//! The chart series, the style panel's pickers and the properties-panel dots all read
//! THIS; none of them carries its own fallback. Precedence per output key:
//!   backend visual_settings.output_styles → node visual_settings.output_styles →
//!   single-output top-level colour (node or backend) → OUTPUT_COLOR_PALETTE by POSITION,
//! so output 1 is the same hue on every indicator that declares nothing.
export const OUTPUT_COLOR_PALETTE = ['#2962FF', '#B417EB', '#00C292', '#E46A76', '#FF9800', '#00BCD4', '#607D8B', '#E91E63'];

export const defaultOutputStyles = (indicatorName, allIndicatorMetadata, visualSettings, nodeOutputs) => {
    const meta = (allIndicatorMetadata || []).find(m => m.name === indicatorName) || {};
    const backend = meta.visual_settings || {};
    const declared = { ...(backend.output_styles || {}), ...(visualSettings?.output_styles || {}) };
    //--> registry order first (stable positions), then any extra keys the node or a declaration carries
    const outputs = [...new Set([
        ...(Array.isArray(meta.outputs) ? meta.outputs : []),
        ...(Array.isArray(nodeOutputs) ? nodeOutputs : []),
        ...Object.keys(declared),
    ])];
    if (outputs.length === 0) outputs.push('value');
    const single = outputs.length === 1 ? (visualSettings?.color || backend.color || null) : null;
    const result = {};
    outputs.forEach((key, i) => {
        const d = declared[key] || {};
        result[key] = d.color ? d : { ...d, color: single || OUTPUT_COLOR_PALETTE[i % OUTPUT_COLOR_PALETTE.length] };
    });
    //--> keys declared for outputs the registry no longer lists stay as declared (never dropped)
    Object.keys(declared).forEach(key => { if (!result[key]) result[key] = declared[key]; });
    return result;
};

export const deriveStrategyIndicators = (nodes, edges, allIndicatorMetadata, resolveSmartLabel) => {
  if (!Array.isArray(nodes) || nodes.length === 0) return [];

  return nodes
      .filter(n => ELIGIBLE_LAYER_NODE_TYPES.includes(n.type))
      .map(n => {
          const indicatorName = n.data?.indicator || n.type || (() => {
              console.warn(`[ChartDerivation] Node ${n.id} missing type, using 'unknown'`);
              return 'unknown';
          })();
          
          const canonicalMeta = allIndicatorMetadata?.find(m => m.name === indicatorName);
          const backendVisuals = canonicalMeta?.visual_settings || {};
          
          const { level_display: _ld, ...restVisualSettings } = (n.data.visual_settings || {});
          //* 50-7: pane placement resolved by the one authority (node setting > backend
          //* metadata > type default; math defaults to a sub-pane) — see nodeVisualConfig.
          const visualSettings = {
              ...restVisualSettings,
              is_overlay: resolveNodeOverlay(n.type, n.data, backendVisuals.is_overlay)
          };
          
          if (backendVisuals.type) {
              const bType = backendVisuals.type.toLowerCase();
              if (bType === 'custom' || bType === 'marker') {
                  visualSettings.type = bType;
              }
          }

          if (!visualSettings.color && backendVisuals.color) {
              visualSettings.color = backendVisuals.color;
          }

           const functionalParams = (() => {
               if (n.type === 'indicator') return n.data.params || {};
               if (['math', 'logic_gate', 'comparator', 'signal', 'position_trigger'].includes(n.type)) {
                   return {
                       operator: n.data.operator,
                       action: n.data.action,
                       inputs: n.data.inputs
                   };
               }
               return n.data || {};
           })();

           //--> 142-49 slice 4: per-output styles resolved ONCE here (defaults + node overrides)
           const outputStyles = { ...defaultOutputStyles(indicatorName, allIndicatorMetadata, visualSettings, n.data.outputs), ...(n.data.outputStyles || {}) };

           return {
               id: n.id,
               nodeType: n.type,
               type: indicatorName,
               sourceId: n.data?.sourceId,
               label: resolveSmartLabel(n, nodes, edges),
               params: functionalParams,
               visual_settings: visualSettings,
               isHidden: !isNodeShownOnChart(n.type, n.data), // 50-7: condition markers opt-in
               is_overlay: visualSettings.is_overlay,
               overlay: visualSettings.is_overlay,
               styleOptions: {
                 ...(n.data.styleOptions || {}),
                 //! 142-49 slice 4: a scalar series with no node/backend colour takes its single
                 //! output's default from the one colour authority (same hue as the panel dot)
                 color: n.data.color || visualSettings.color || Object.values(outputStyles)[0]?.color,
                 lineWidth: n.data.lineWidth || visualSettings.line_width,
                 lineStyle: n.data.lineStyle ?? visualSettings.line_style,
                 lineType: n.data.lineType,
                 chartType: (
                     visualSettings.type?.toLowerCase() === 'custom' ? 'custom' : 
                     visualSettings.type?.toLowerCase() === 'marker' ? 'marker' :
                     (['comparator', 'logic_gate', 'signal', 'position_trigger'].includes(n.type)) ? 'marker' :
                     (n.data.chartType || (
                         ['DUAL_AREA', 'dual_area'].includes(visualSettings.type) ? 'dual_area' :
                         ['BAND', 'band', 'AREA', 'area'].includes(visualSettings.type) ? 'area' : 
                         ['HISTOGRAM', 'histogram'].includes(visualSettings.type) ? 'histogram' : 
                         'line'
                     ))
                 ),
                 fillColor: n.data.fillColor || visualSettings.fill_color,
                 topColor: n.data.topColor || visualSettings.fill_color,
                 outputStyles, // 142-37: backend colours; 142-49 slice 4: palette-completed are the default, user styles win per key
                 outputColors: n.data.outputColors || {},
                 outputLabels: backendVisuals.output_labels || {}, // 142-37: one label authority (backend metadata)
                 scalarOutputKey: n.data.output || null, // 142-37: where legacy scalar history rows land
                 markerStates: backendVisuals.marker_states?.length
                     ? backendVisuals.marker_states
                     : (visualSettings.marker_states || [])
               }
           };
      })
      .sort((a, b) => (a.isHidden ? 1 : 0) - (b.isHidden ? 1 : 0));
};

/*>>> VISUAL OVERRIDE MERGE / DIFF (50-6 §C) <<<*/
//! D40/AC-M2-3/AC-M2-4 — RFC 7396 JSON MERGE PATCH semantics end to end. A `null` PATCH
//! VALUE DELETES that key from the result — a first-class instruction, not a value to be
//! silently filtered out before it ever reaches here. Any nested PLAIN OBJECT merges
//! recursively; any ARRAY value replaces WHOLESALE, never index-merged. `diffPatch`'s own
//! recursion depth below MUST stay symmetric with this.
function isPlainObject(v) { return v !== null && typeof v === 'object' && !Array.isArray(v); }

// Exported — §H's Save-time adapter calls this directly to build its diff baseline and
// candidate (same ONE merge rule, not a second helper).
export function patchNodeData(data, patch) {
  if (!patch) return data;
  const next = { ...data };
  for (const [k, v] of Object.entries(patch)) {
    if (v === null) { delete next[k]; continue; }   // RFC 7396: null in the patch DELETES the key
    next[k] = isPlainObject(v) ? patchNodeData(data?.[k] || {}, v) : v;
  }
  return next;
}

//! 50-8 (R8-T5, D23) — overrides are DISPLAY-ONLY and must never change what a surface COMPUTES or
//! WHICH node/value it reads. The ONE gate is a positive allow-list: only members of the
//! backend-owned `VISUAL_DATA_KEYS` (mirrored in config/visualDataKeys.json; a contract test fails
//! CI on drift) are ever applied from a record. This replaces the former deny-list
//! (the old frontend computation-key deny-list + its stripper, DECOMMISSIONED): a computation key
//! (indicator, params, operator, ...) is simply not a member, so its value — including a null
//! delete — is ignored here and rejected (422) by the server.
export const VISUAL_DATA_KEYS = Object.freeze([...visualDataKeys]);
const VISUAL_DATA_KEY_SET = new Set(VISUAL_DATA_KEYS);

//--> keep ONLY the mirrored members of a node patch/entry (null leaves are kept — RFC 7396 delete)
export function pickVisualMembers(entry) {
  if (!isPlainObject(entry)) return {};
  const out = {};
  for (const [k, v] of Object.entries(entry)) {
    if (VISUAL_DATA_KEY_SET.has(k)) out[k] = v;
  }
  return out;
}

//--> D20 / R6-T2 — a node counts as "overridden" only when its record entry holds a STYLING member (any
//    VISUAL_DATA_KEYS member except the naming pair custom_name / notes). ONE predicate shared by the
//    Live Analysis panel dot and the Builder's styling-controls badge. A named-only entry shows no badge.
const NAMING_KEYS = ['custom_name', 'notes'];
export const hasStylingOverride = (entry) => !!entry && VISUAL_DATA_KEYS.some(
  (k) => !NAMING_KEYS.includes(k) && entry[k] !== undefined && entry[k] !== null,
);

//* HELPER 1 — rendering. Baseline for the EFFECTIVE node a display consumer sees:
//* version (n.data) ⊕ saved ⊕ draft. D35: this is the ONLY upstream merge point — never
//* a 2nd/3rd independent reader of overrides. A node with no applicable entry is returned AS-IS
//* (same object) so referential equality survives and memoised consumers do not re-render.
export function applyVisualOverrides(nodes, saved = {}, draft = {}) {
  return (nodes || []).map(n => {
    const s = pickVisualMembers(saved?.[n.id]);
    const d = pickVisualMembers(draft?.[n.id]);
    if (Object.keys(s).length === 0 && Object.keys(d).length === 0) return n;
    return { ...n, data: patchNodeData(patchNodeData(n.data, s), d) };
  });
}

//* HELPER 2 — diffing (AC-M2-3/AC-M2-4 FIX). `stripNulls` is a data-hygiene pass only, run
//* on BOTH `baselineData` and `candidateData` BEFORE comparison, to clean stray literal
//* `null`s already present in raw version/saved data (pre-existing data facts, never a
//* user's deletion request) so they never leak into either side of the comparison. The
//* DIFF OUTPUT's own `null`s (emitted below, one per key present in the cleaned baseline
//* but ABSENT from the cleaned candidate) ARE the RFC 7396 deletion signal, and are never
//* themselves stripped.
function deepEqual(a, b) { return JSON.stringify(a) === JSON.stringify(b); }
function stripNulls(value) {
  if (value === null || value === undefined) return undefined;
  if (Array.isArray(value)) return value.map(stripNulls).filter((v) => v !== undefined);
  if (isPlainObject(value)) {
    const out = {};
    for (const [k, v] of Object.entries(value)) { const sv = stripNulls(v); if (sv !== undefined) out[k] = sv; }
    return out;
  }
  return value;
}
export function diffPatch(baselineData, candidateData) {
  const cleanBaseline = stripNulls(baselineData) || {};    // hygiene pass on BOTH sides — never a delete signal
  const cleanCandidate = stripNulls(candidateData) || {};
  const out = {};
  for (const [k, v] of Object.entries(cleanCandidate)) {
    if (isPlainObject(v) && isPlainObject(cleanBaseline[k])) {
      const subDiff = diffPatch(cleanBaseline[k], v);            // recurse into ANY nested object, any depth
      if (Object.keys(subDiff).length) out[k] = subDiff;
    } else if (!deepEqual(v, cleanBaseline[k])) {
      out[k] = v;                                                // arrays still replace wholesale
    }
  }
  for (const k of Object.keys(cleanBaseline)) {
    if (!(k in cleanCandidate)) out[k] = null;   // RFC 7396: present in baseline, absent in candidate → DELETE
  }
  return out;
}
